import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Mic, MessageSquare, BookOpen, Layers, ArrowRight, Flame, Mail, Plus } from "lucide-react";
import { useAuth } from "../lib/auth";
import SpeakButton from "../components/SpeakButton";
import PricingSection from "../components/PricingSection";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_91fc1071-a755-4ee2-8eeb-0c8b2e7c23e2/artifacts/frr6xg90_Product%20Icon%401x.png";

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const goStart = () => {
    if (user) navigate("/app");
    else navigate("/register");
  };

  return (
    <div className="min-h-screen bg-navy-900 text-white grain relative overflow-hidden">
      {/* Background gradient blobs */}
      <div className="pointer-events-none absolute -top-40 -left-40 h-[480px] w-[480px] rounded-full bg-peach-500/10 blur-3xl" />
      <div className="pointer-events-none absolute top-1/2 -right-40 h-[520px] w-[520px] rounded-full bg-peach-500/8 blur-3xl" />

      {/* Header */}
      <header className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
          <img src={LOGO_URL} alt="Avoir la pêche" className="h-12 w-12 rounded-2xl border border-white/10" />
          <div>
            <div className="font-heading font-bold text-lg leading-tight">Avoir la pêche</div>
          </div>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/dictionary"
            data-testid="landing-dictionary-link"
            className="hidden sm:inline-flex px-4 sm:px-5 py-2.5 rounded-full text-sm text-white border border-white/15 hover:bg-white/5 transition"
          >
            Dictionary
          </Link>
          <Link
            to="/login"
            data-testid="landing-login-link"
            className="px-4 sm:px-5 py-2.5 rounded-full text-sm text-white border border-white/15 hover:bg-white/5 transition"
          >
            Sign in
          </Link>
          <button
            onClick={goStart}
            data-testid="landing-cta-header"
            className="px-4 sm:px-5 py-2.5 rounded-full text-sm font-semibold bg-peach-500 text-navy-900 hover:bg-peach-400 transition peach-glow"
          >
            Get started
          </button>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-peach-500/10 border border-peach-500/30 text-peach-200 text-xs tracking-[0.2em] uppercase font-semibold">
              <Sparkles className="h-3.5 w-3.5" />
              AI · Voice · Lessons · CEFR A1 → C2
            </div>
            <h1 className="mt-6 font-heading font-bold text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02]">
              Learn French.<br />
              <span className="gradient-text">Avoir la pêche.</span>
            </h1>
            <p className="mt-6 text-lg text-[#A89BC9] max-w-xl leading-relaxed">
              An AI tutor who speaks French with you, listens when you speak, and corrects
              your grammar with kindness. Learn by conversing — not by drilling.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                onClick={goStart}
                data-testid="landing-cta-main"
                className="px-7 py-4 rounded-full bg-peach-500 text-navy-900 font-semibold hover:bg-peach-400 peach-glow transition flex items-center gap-2"
              >
                Start free
                <ArrowRight className="h-4 w-4" />
              </button>
              <Link
                to="/demo"
                data-testid="landing-demo-cta"
                className="px-7 py-4 rounded-full border border-mint-400/40 text-mint-300 hover:bg-mint-400/5 transition flex items-center gap-2"
              >
                <Sparkles className="h-4 w-4" />
                Try the demo
              </Link>
              <Link
                to="/login"
                className="px-7 py-4 rounded-full border border-white/15 hover:bg-white/5 transition text-white"
                data-testid="landing-secondary-cta"
              >
                I already have an account
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {[
                { k: "6", v: "levels" },
                { k: "24/7", v: "AI tutor" },
                { k: "EN+FR", v: "bilingual" },
              ].map((it) => (
                <div key={it.v} className="glass rounded-2xl p-4 text-center">
                  <div className="font-heading text-2xl font-bold text-peach-500">{it.k}</div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#A89BC9] mt-1">{it.v}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -inset-6 bg-peach-500/15 rounded-[3rem] blur-3xl" />
              <div className="relative bg-navy-800 border border-white/8 rounded-3xl p-8 shadow-2xl">
                <div className="flex items-center gap-2 mb-6">
                  <div className="h-2.5 w-2.5 rounded-full bg-peach-500" />
                  <div className="text-xs tracking-[0.3em] uppercase text-[#A89BC9]">AI Conversation</div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-start">
                    <div className="bg-white/8 border border-white/5 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%]">
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <div className="text-sm">Bonjour ! Comment ça va aujourd'hui ?</div>
                          <div className="text-xs text-[#A89BC9] mt-1">Hello! How are you today?</div>
                        </div>
                        <SpeakButton
                          text="Bonjour ! Comment ça va aujourd'hui ?"
                          size="xs"
                          data-testid="landing-hero-listen"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="bg-peach-500/15 border border-peach-500/25 rounded-2xl rounded-tr-sm px-4 py-3 max-w-[85%]">
                      <div className="text-sm text-peach-200">Je vais bien merci, et tois?</div>
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-white/8 border border-white/5 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[90%]">
                      <div className="text-xs uppercase tracking-[0.2em] text-peach-500 font-semibold mb-1">Correction</div>
                      <div className="text-sm">"et <span className="text-peach-300 font-semibold">toi</span>?" — without the "s".</div>
                      <div className="text-sm mt-3">Très bien ! Moi aussi je vais bien.</div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/8">
                  <div className="flex items-center gap-1">
                    {[12, 22, 18, 28, 14, 24, 16].map((h, i) => (
                      <span key={i} className="wave-bar" style={{ height: h }} />
                    ))}
                  </div>
                  <div className="text-xs text-[#A89BC9] flex-1">Recording…</div>
                  <div className="h-9 w-9 rounded-full bg-peach-500 flex items-center justify-center peach-glow">
                    <Mic className="h-4 w-4 text-navy-900" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features bento */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { icon: MessageSquare, title: "Chat with the AI", desc: "A natural conversation tuned to your level. Instant grammar fixes and cultural tips." },
            { icon: Mic, title: "Speak, hear yourself", desc: "Record your voice, get a transcript and a pronunciation score." },
            { icon: BookOpen, title: "Structured lessons", desc: "Vocabulary, grammar, scenarios — from cafés to airports, A1 through C2." },
            { icon: Layers, title: "Smart flashcards", desc: "Memorize without effort with our flip-animated cards and quiz mode." },
            { icon: Flame, title: "Daily streaks", desc: "Build a habit. Every consecutive day grows your streak and motivation." },
            { icon: Sparkles, title: "English-friendly", desc: "All UI in English. Translations for beginners. We gently push you toward French." },
          ].map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="bg-navy-800 border border-white/8 rounded-3xl p-7 hover:border-peach-500/30 hover:-translate-y-1 transition-all"
            >
              <div className="h-11 w-11 rounded-2xl bg-peach-500/15 border border-peach-500/30 flex items-center justify-center mb-5">
                <f.icon className="h-5 w-5 text-peach-300" />
              </div>
              <div className="font-heading font-semibold text-xl mb-2">{f.title}</div>
              <div className="text-sm text-[#A89BC9] leading-relaxed">{f.desc}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <PricingSection />

      {/* FAQ */}
      <section id="faq" className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 pb-12">
        <div className="text-center mb-12">
          <div className="text-xs tracking-[0.3em] uppercase text-peach-500 font-bold mb-3">FAQ</div>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight">
            Everything you were <span className="gradient-text">wondering</span>.
          </h2>
          <p className="mt-4 text-[#A89BC9] max-w-xl mx-auto">
            Short, honest answers. Still curious? Write to us at{" "}
            <a href="mailto:hello@avoirlapeche.com" className="text-peach-300 hover:text-peach-400 underline decoration-dotted">
              hello@avoirlapeche.com
            </a>
            .
          </p>
        </div>
        <FAQList />
      </section>

      {/* For creators — embeddable widget */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 pb-24">
        <div className="rounded-3xl p-8 md:p-12 bg-gradient-to-br from-[#1a0e33] via-[#2a1248] to-[#1a0e33] border border-peach-500/20 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-peach-500/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-violet-500/20 blur-3xl" />
          <div className="relative z-10 grid md:grid-cols-[1.2fr_1fr] items-center gap-10">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[10px] tracking-[0.22em] uppercase text-peach-200 font-semibold">
                For teachers & creators · free
              </div>
              <h3 className="mt-5 font-heading font-bold text-3xl md:text-4xl tracking-tight">
                Drop a <span className="gradient-text">pronunciation widget</span> into any post.
              </h3>
              <p className="mt-4 text-[#cdc1ed] text-base leading-relaxed">
                One line of HTML. Your readers hear a phrase in native French, record themselves,
                and get an instant accuracy score with pronunciation tips — all without leaving your site.
              </p>
              <Link
                to="/embed"
                data-testid="landing-embed-cta"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-peach-500 text-navy-900 font-semibold hover:bg-peach-400 transition peach-glow"
              >
                Build your embed <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/10 bg-black/40">
              <iframe
                title="Pronunciation widget demo"
                src="/embed/pronounce?phrase=J%27aime%20me%20promener%20au%20bord%20de%20la%20Seine.&en=I%20love%20strolling%20along%20the%20Seine.&level=A2&ref=landing"
                width="100%"
                height="280"
                style={{ border: 0, display: "block" }}
                allow="microphone"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="bg-gradient-to-br from-navy-800 to-navy-700 border border-white/10 rounded-[2.5rem] p-10 md:p-16 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-peach-500 font-bold mb-3">Ready?</div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight">Start your first dialogue<br />in 30 seconds.</h2>
          </div>
          <button
            onClick={goStart}
            data-testid="landing-cta-bottom"
            className="px-8 py-4 rounded-full bg-peach-500 text-navy-900 font-semibold hover:bg-peach-400 peach-glow transition flex items-center gap-2 whitespace-nowrap"
          >
            Create my account
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6 text-xs text-[#A89BC9]">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Avoir la pêche" className="h-8 w-8 rounded-xl border border-white/10" />
            <div className="text-left">
              <div className="font-heading font-bold text-white text-sm leading-tight">Avoir la pêche</div>
              <div>© {new Date().getFullYear()} · by <span className="text-white font-semibold">Sarv Singh</span></div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
            <Link to="/dictionary" data-testid="footer-dictionary-link" className="hover:text-peach-300 transition">Dictionary</Link>
            <Link to="/demo" className="hover:text-peach-300 transition">Try the demo</Link>
            <Link to="/embed" data-testid="footer-embed-link" className="hover:text-peach-300 transition">Embed widget</Link>
            <a href="#faq" className="hover:text-peach-300 transition">FAQ</a>
            <a
              href="mailto:hello@avoirlapeche.com"
              data-testid="footer-contact-email"
              className="flex items-center gap-1.5 hover:text-peach-300 transition"
            >
              <Mail className="h-3.5 w-3.5" />
              hello@avoirlapeche.com
            </a>
          </div>
        </div>
        <div className="mt-6 text-center text-[10px] text-[#A89BC9]/70 tracking-wide">
          Made with <span className="text-peach-500">♥</span> for francophiles
        </div>
      </footer>
    </div>
  );
}

const FAQS = [
  {
    q: "Do I need to speak any French to start?",
    a: "Not at all. Pick A1 when you sign up and the AI will start from the absolute basics — greetings, numbers, simple dialogues — adapting its pace to you.",
  },
  {
    q: "How is this different from Duolingo?",
    a: "Avoir la pêche is built around real conversations, not multiple-choice drills. You talk (in writing or with your voice), the AI replies in French, and you get gentle grammar fixes with cultural notes. Think of it as a patient bilingual tutor on call 24/7.",
  },
  {
    q: "Does the AI correct my pronunciation?",
    a: "Yes. Record yourself in Speak mode or the Daily Challenge and our system transcribes what you said, compares it to the target phrase, and returns an accuracy score plus short pronunciation tips.",
  },
  {
    q: "Can I use the Dictionary for free?",
    a: "Yes — the full 'Word of the Day' dictionary (with examples, search and audio) is available without an account. Hit the Dictionary link in the top nav.",
  },
  {
    q: "What levels are covered?",
    a: "Structured lessons span CEFR A1 → C1 today. The AI chat adapts to every level, including C2 if you change your level in your profile.",
  },
  {
    q: "How does the streak work?",
    a: "Any learning activity (chat, voice, lessons, flashcards) keeps your daily streak alive. The Daily Challenge has its own separate streak with trophies unlocked at 7, 30 and 100 days.",
  },
  {
    q: "Is my voice data stored?",
    a: "Only briefly, to transcribe and score your speech. We do not sell, share, or reuse voice recordings for model training.",
  },
  {
    q: "How do I contact the team?",
    a: "Write to hello@avoirlapeche.com — we read every message and usually reply the same day.",
  },
];

function FAQList() {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-3" data-testid="faq-list">
      {FAQS.map((item, idx) => {
        const isOpen = open === idx;
        return (
          <div
            key={item.q}
            className={`rounded-2xl border transition-all ${
              isOpen
                ? "bg-gradient-to-br from-navy-800 to-navy-700 border-peach-500/30"
                : "bg-navy-800/60 border-white/8 hover:border-white/20"
            }`}
          >
            <button
              type="button"
              onClick={() => setOpen(isOpen ? -1 : idx)}
              data-testid={`faq-q-${idx}`}
              className="w-full flex items-start justify-between gap-4 px-5 md:px-6 py-5 text-left"
              aria-expanded={isOpen}
            >
              <span className={`font-heading text-base md:text-lg ${isOpen ? "text-white" : "text-white/90"}`}>
                {item.q}
              </span>
              <span
                className={`mt-1 h-7 w-7 shrink-0 rounded-full flex items-center justify-center transition ${
                  isOpen ? "bg-peach-500 text-navy-900 rotate-45" : "bg-white/5 text-[#A89BC9]"
                }`}
              >
                <Plus className="h-4 w-4" />
              </span>
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  key="content"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <div className="px-5 md:px-6 pb-5 -mt-1 text-sm md:text-base text-[#CDC1ED] leading-relaxed">
                    {item.a}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}
